<template>
    <div
        v-if="!hasStartedInlineCheckout || isCreatingCheckout || isLoading"
        class="mx-auto mt-12 max-w-screen-xl"
    >
        <prime-skeleton />
    </div>

    <checkout-checking-payment
        v-else-if="checkoutStatus === 'ARMED'"
        :checkout-id="checkoutId"
        @checkout-success="handleCheckoutStatusChanged"
        @checkout-status-changed="handleCheckoutStatusChanged"
    />

    <checkout-processing-payment
        v-else-if="checkoutStatus === 'PROCESSING'"
        :checkout-id="checkoutId"
        @checkout-success="handleCheckoutStatusChanged"
        @checkout-status-changed="handleCheckoutStatusChanged"
    />

    <checkout-payment-recovery-error v-else-if="checkoutStatus === 'ERROR'" />

    <div
        v-else-if="completedCheckout !== undefined"
        class="mx-auto mt-12 max-w-screen-xl"
    >
        <!-- The completion watcher owns kiosk navigation; keep this transition visually stable. -->
        <prime-skeleton />
    </div>

    <div v-else>
        <app-error v-if="data === undefined" />
        <app-error v-else-if="checkout === undefined" />
        <not-found v-else-if="checkout === null" />
        <purchase-contract-section v-else>
            <template #title>{{ t('cartSidebar.checkout') }}</template>
            <div
                class="grid grid-cols-1"
                :class="isKiosk ? '' : 'xl:grid-cols-2'"
            >
                <div :class="isKiosk ? '' : 'xl:justify-self-end'">
                    <div
                        :class="
                            isKiosk
                                ? ''
                                : 'max-w-[650px] sm:mx-auto xl:mx-0 xl:min-w-[650px] xl:max-w-[650px] xl:px-8 xl:py-12'
                        "
                    >
                        <nuxt-error-boundary @error="logError">
                            <checkout-form
                                :query="data"
                                :checkout="checkout"
                                :contract="contract"
                                :balance-due-mode="balanceDueMode"
                                @kiosk-checkout-success="handleCheckoutSuccess"
                                @checkout-status-changed="
                                    handleCheckoutStatusChanged
                                "
                            />

                            <template #error>
                                <app-error />
                            </template>
                        </nuxt-error-boundary>
                    </div>
                </div>

                <div
                    class="order-first bg-gray-50"
                    :class="[
                        isKiosk ? '' : 'xl:order-2 xl:border-l xl:bg-white'
                    ]"
                >
                    <div
                        class="max-w-[650px]"
                        :class="
                            isKiosk
                                ? ''
                                : 'sm:mx-auto xl:mx-0 xl:min-w-[650px] xl:max-w-[650px] xl:px-8 xl:py-12'
                        "
                    >
                        <checkout-cart
                            :checkout="checkout"
                            :balance-due-mode="balanceDueMode"
                        />
                    </div>
                </div>
            </div>
        </purchase-contract-section>
    </div>
</template>

<script setup lang="ts">
import { useSelectedLanguage } from '~/composables/use-selected-language'
import {
    useActiveFacility,
    useKioskSuccess,
    useLayoutMode,
    useStorefront
} from '#imports'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { FragmentType } from '~/resources/graphql'
import { graphql } from '~/resources/graphql'
import { computed, onMounted, ref, watch } from 'vue'
import PrimeSkeleton from 'primevue/skeleton'
import { useRpQuery } from '~/composables/graphql'
import CheckoutCart from '~/components/Checkout/Cart/CheckoutCart.vue'
import CheckoutCheckingPayment from '~/components/Checkout/CheckoutCheckingPayment.vue'
import CheckoutForm from '~/components/Checkout/CheckoutForm.vue'
import CheckoutPaymentRecoveryError from '~/components/Checkout/CheckoutPaymentRecoveryError.vue'
import CheckoutProcessingPayment from '~/components/Checkout/CheckoutProcessingPayment.vue'
import NuxtErrorBoundary from '#app/components/nuxt-error-boundary'
import AppError from '~/components/NotFound/AppError.vue'
import { useLogError } from '~/composables/use-log-error'
import { CheckoutSuccessKioskDisplayFragment } from '~/components/Checkout/CheckoutSuccessKioskDisplay.vue'
import NotFound from '~/components/NotFound/NotFound.vue'
import { provideCheckoutPaymentData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import { DocumentSectionContractFragment } from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    contract: FragmentType<typeof DocumentSectionContractFragment>
}>()

const { t } = useI18n({ useScope: 'global' })
const activeFacility = useActiveFacility()
const query = useRpQuery()

const { isKiosk } = useLayoutMode()
const logError = useLogError()

// The contract remains available to the embedded checkout form after creation.
const contractId = computed(() => props.contract.id)
const selectedLanguage = useSelectedLanguage()
const checkoutId = ref('')
const hasStartedInlineCheckout = ref(false)
const queryClient = useQueryClient()
const { mutate: createInlineCheckout, isPending: isCreatingCheckout } =
    useMutation({
        mutationFn: async () => {
            const response = await query(
                graphql(/* GraphQL */ `
                    mutation CreateInlineCheckout($contractId: ID!) {
                        createInlineCheckout(contractId: $contractId) {
                            recordId
                        }
                    }
                `),
                { contractId: contractId.value }
            )

            return response.createInlineCheckout.recordId
        },
        onSuccess(createdCheckoutId) {
            // Draft checkout identities are reused, so discard any quote cached before contract pricing changed.
            // Enabling the query only after removal keeps payment fields hidden until a fresh response arrives.
            queryClient.removeQueries({
                queryKey: ['InlineCheckoutQuery', createdCheckoutId]
            })
            checkoutId.value = createdCheckoutId
        }
    })

const { data, isLoading, refetch } = useQuery({
    queryKey: [
        'InlineCheckoutQuery',
        checkoutId,
        activeFacility,
        selectedLanguage
    ],
    enabled: () => checkoutId.value !== '',
    // Kiosk and inline hosted payments keep resolving after preflight changes the checkout
    // status, so only their explicit recovery paths may replace the active checkout form.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query InlineCheckoutQuery(
                    $facilityId: ID!
                    $checkoutId: ID!
                    $language: Language!
                ) {
                    ...CheckoutFormQueryFragment
                    checkout(id: $checkoutId) {
                        __typename
                        ... on Checkout {
                            status
                            ...CheckoutFormCheckoutFragment
                            ...CheckoutCartFragment
                        }
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessKioskDisplayFragment
                            contracts {
                                ... on PlanContract {
                                    referenceNumber
                                    planType
                                    customer {
                                        firstName
                                    }
                                }
                            }
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                checkoutId: checkoutId.value,
                language: selectedLanguage.value
            }
        )
})

onMounted(() => {
    // Creating a checkout is a browser action, never a server-rendered query side effect.
    hasStartedInlineCheckout.value = true
    createInlineCheckout()
})

const checkout = computed(() => {
    if (data.value === undefined) {
        return undefined
    } else if (data.value.checkout === null) {
        return null
    } else {
        return data.value.checkout.__typename === 'Checkout'
            ? data.value.checkout
            : null
    }
})

const checkoutStatus = computed(() => {
    return data.value?.checkout?.__typename === 'Checkout'
        ? data.value.checkout.status
        : undefined
})

const completedCheckout = computed(() => {
    return data.value?.checkout?.__typename === 'CommitCheckoutResult'
        ? data.value.checkout
        : undefined
})

provideCheckoutPaymentData()

const goToSuccess = useKioskSuccess()

function handleCheckoutSuccess(
    data: FragmentType<typeof CheckoutSuccessKioskDisplayFragment>
) {
    goToSuccess({
        includedInvoice: true,
        wasGuest: false,
        data
    })
}

async function handleCheckoutStatusChanged(): Promise<void> {
    // Admission, provider recovery, and processing children only signal state; this query owns rendering.
    await refetch()
}

const storefront = useStorefront()
const balanceDueMode = computed<boolean>(
    () => isKiosk.value && !storefront.value.collectMoney
)

const hasHandledCompletedCheckout = ref(false)
watch(
    completedCheckout,
    result => {
        if (result === undefined || hasHandledCompletedCheckout.value) {
            return
        }

        hasHandledCompletedCheckout.value = true
        if (!balanceDueMode.value) {
            // Paid inline recovery uses the same invoice-aware kiosk result as the direct mutation response.
            handleCheckoutSuccess(result)
            return
        }

        const completedContract = result.contracts.find(
            contract => contract.__typename === 'PlanContract'
        )
        if (
            completedContract === undefined ||
            completedContract.__typename !== 'PlanContract'
        ) {
            // Balance-due inline checkout only supports a single plan contract.
            logError(
                new Error(
                    'Completed inline checkout is missing its plan contract.'
                )
            )
            return
        }

        goToSuccess({
            includedInvoice: false,
            wasGuest: false,
            data: {
                referenceNumber: completedContract.referenceNumber,
                planType: completedContract.planType,
                firstName: completedContract.customer.firstName
            }
        })
    },
    { immediate: true }
)
</script>
